// MONITORING-P1 on the run page (docs/design/2026-11-01-monitoring-in-companyos.md §3): what the
// DE's host reported for the run (`runs.telemetry`). Its events join the steps timeline by time
// (`mergeTimeline`, `EventItem`); each detached job gets a card (stages, slide scores, redos,
// cost, delivery); "Stop on host" asks the DE's bridge to stop the run where it runs
// (`runs.requestStop`), and the page then follows the request (waiting for the bridge, picked
// up, stopped, could not stop, expired); the run's open alerts sit on top as a notice.
import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  cn,
  Field,
  FieldDescription,
  FieldLabel,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BellRingIcon,
  CheckCheckIcon,
  CircleStopIcon,
  ClipboardCheckIcon,
  LayersIcon,
  type LucideIcon,
  PackageCheckIcon,
  PlayIcon,
  RadioIcon,
  RedoIcon,
  SendIcon,
  StarIcon,
  WalletIcon,
  WrenchIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { RunStepRow } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { Facts, Section } from "../screen-parts";
import { useSession } from "../use-feature";
import { formatElapsed, formatMicros, formatTokens } from "./agent-parts";
import { invalidateMonitoring, runTelemetryQuery } from "./agent-queries";

export type RunTelemetry = OperationOutput<"runs.telemetry">;
export type TelemetryEvent = RunTelemetry["events"][number];
export type MonitorJob = RunTelemetry["jobs"][number];
export type StopRequest = RunTelemetry["stop_requests"][number];
export type MonitorAlert = RunTelemetry["alerts"][number];

/** A stop request the bridge has not answered yet. */
export const stopIsLive = (s: StopRequest) => s.state === "pending" || s.state === "picked";

/** The run's telemetry; polled every 5 s while a stop request waits for the bridge. */
export function useRunTelemetry(api: Api, co: string, runId: string) {
  return useQuery({
    ...runTelemetryQuery(api, co, runId),
    refetchInterval: (q) => (q.state.data?.stop_requests.some(stopIsLive) ? 5_000 : false),
  });
}

// Timeline ---------------------------------------------------------------------------------------

export type TimelineEntry =
  | { kind: "step"; at: string; step: RunStepRow }
  | { kind: "event"; at: string; event: TelemetryEvent };

/** The run's steps and its host's events in one list, oldest first (steps first on a tie). */
export function mergeTimeline(
  steps: readonly RunStepRow[],
  events: readonly TelemetryEvent[],
): TimelineEntry[] {
  const all: (TimelineEntry & { order: number })[] = [
    ...steps.map((step, i) => ({ kind: "step" as const, at: step.created_at, step, order: i })),
    ...events.map((event, i) => ({
      kind: "event" as const,
      at: event.occurred_at,
      event,
      order: steps.length + i,
    })),
  ];
  return all
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.order - b.order)
    .map(({ order: _o, ...e }) => e);
}

const EVENT_ICONS: Record<string, LucideIcon> = {
  "run.wake_pushed": BellRingIcon,
  "run.checked_out": PlayIcon,
  "run.completed": CheckCheckIcon,
  "step.tool": WrenchIcon,
  "outbox.posted": SendIcon,
  "send_now.sent": SendIcon,
  "stop.acked": CircleStopIcon,
  "job.started": LayersIcon,
  "job.stage_started": LayersIcon,
  "job.stage_ended": LayersIcon,
  "job.slide_scored": StarIcon,
  "job.redo": RedoIcon,
  "job.cost": WalletIcon,
  "job.delivered": PackageCheckIcon,
  "job.done": ClipboardCheckIcon,
};

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" ? v : null);
const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;

/** One line for what an event says, from its type and data (never a payload dump). */
export function eventText(e: TelemetryEvent, currency: string | undefined): string {
  const d = e.data;
  const thread = str(d.thread_id);
  switch (e.type) {
    case "run.wake_pushed":
      return `Wake sent to the host${str(d.subject) ? `: ${d.subject}` : ""}`;
    case "run.checked_out":
      return "Picked up on the host";
    case "run.completed":
      return "Finished on the host";
    case "step.tool": {
      const ms = num(d.duration_ms);
      return `${str(d.tool) ?? "Tool"}${ms !== null ? ` · ${seconds(ms)}` : ""}${d.ok === false ? " · failed" : ""}`;
    }
    case "outbox.posted":
      return `Posted${thread ? ` to ${thread}` : ""} through the outbox`;
    case "send_now.sent":
      return `Sent now${thread ? ` to ${thread}` : ""}`;
    case "steer.acked":
      return "Steer received";
    case "stop.acked": {
      const ms = num(d.latency_ms);
      const how = [str(d.method), ms !== null ? seconds(ms) : null].filter(Boolean).join(", ");
      return `${d.ok === false ? "Could not stop on the host" : "Stopped on the host"}${how ? ` (${how})` : ""}`;
    }
    case "job.started": {
      const total = num(d.slides_total);
      return `Job started: ${str(d.job_kind) ?? "job"}${total !== null ? `, ${total} slides` : ""}`;
    }
    case "job.stage_started": {
      const budget = num(d.budget_s);
      return `Stage ${str(d.stage) ?? "?"} started (try ${num(d.attempt) ?? 1}${budget ? `, ${Math.round(budget / 60)} min budget` : ""})`;
    }
    case "job.stage_ended":
      return `Stage ${str(d.stage) ?? "?"} ${d.ok === false ? "failed" : "done"}`;
    case "job.slide_scored": {
      const score = num(d.score);
      const pass = d.pass === true ? " · pass" : d.pass === false ? " · fail" : "";
      return `Slide ${num(d.slide) ?? "?"} scored ${score ?? "—"}${str(d.critic) ? ` (${d.critic})` : ""}${pass}`;
    }
    case "job.redo":
      return `Slide ${num(d.slide) ?? "?"} redone${str(d.reason) ? `: ${d.reason}` : ""}`;
    case "job.cost": {
      const tokens = (num(d.tokens_in) ?? 0) + (num(d.tokens_out) ?? 0);
      return `Cost ${formatMicros(num(d.micros) ?? 0, currency)}${str(d.model) ? ` · ${d.model}` : ""}${tokens ? ` · ${formatTokens(tokens)} tokens` : ""}`;
    }
    case "job.delivered": {
      const files = num(d.files);
      const posts = Array.isArray(d.post_ids) ? d.post_ids.length : 0;
      return `Delivered${files !== null ? ` ${files} files` : ""}${thread ? ` to ${thread}` : ""}${posts ? ` · ${posts} post${posts === 1 ? "" : "s"}` : ""}`;
    }
    case "job.done":
      return `Job ${d.status === "ok" ? "finished" : d.status === "cancelled" ? "cancelled" : "failed"}`;
    default:
      return e.message ?? e.type;
  }
}

const LEVEL_BADGE = {
  warn: ["Warning", "warning"],
  error: ["Error", "danger"],
} as const;

/** One host event on the run's timeline (`<li>`, next to the steps). */
export function EventItem({
  event: e,
  currency,
}: {
  event: TelemetryEvent;
  currency: string | undefined;
}) {
  const Icon = EVENT_ICONS[e.type] ?? RadioIcon;
  const text = eventText(e, currency);
  const level = e.level === "warn" || e.level === "error" ? LEVEL_BADGE[e.level] : null;
  return (
    <li
      className="relative flex gap-3 border-l border-border pb-4 pl-5 last:pb-0"
      data-event={e.type}
      data-level={e.level}
    >
      <span
        className={cn(
          "absolute top-0 -left-3 flex size-6 items-center justify-center rounded-full border bg-card",
          e.level === "error"
            ? "border-danger-text"
            : e.level === "warn"
              ? "border-warning-text"
              : "border-border",
        )}
      >
        <Icon className="size-3.5 text-muted-foreground" aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">Host</span>
          <code className="font-mono text-2xs text-muted-foreground">{e.type}</code>
          {level ? <Badge variant={level[1]}>{level[0]}</Badge> : null}
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatDateTime(e.occurred_at)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{text}</p>
        {e.message && e.message !== text ? (
          <p
            className={cn(
              "text-sm",
              e.level === "error"
                ? "text-danger-text"
                : e.level === "warn"
                  ? "text-warning-text"
                  : "text-muted-foreground",
            )}
            data-slot="event-message"
          >
            {e.message}
          </p>
        ) : null}
      </div>
    </li>
  );
}

// Job cards --------------------------------------------------------------------------------------

const JOB_STATUS: Record<
  MonitorJob["status"],
  [string, "info" | "success" | "danger" | "neutral"]
> = {
  running: ["Running", "info"],
  done: ["Done", "success"],
  failed: ["Failed", "danger"],
  cancelled: ["Cancelled", "neutral"],
};

const score = (n: number | null) => (n === null ? "—" : n.toFixed(1));

/** A detached job's card: where it is, its stages and slides, what it cost and where it went. */
export function JobCard({ job: j, currency }: { job: MonitorJob; currency: string | undefined }) {
  const [label, tone] = JOB_STATUS[j.status];
  const failedSlides = j.slides.filter((s) => s.pass === false).length;
  const facts = [
    {
      label: "Slides",
      value:
        j.slides_total !== null
          ? `${j.slides_done} of ${j.slides_total} done`
          : `${j.slides_done} done`,
    },
    {
      label: "Scores",
      value: j.slides.length
        ? `min ${score(j.score_min)} · avg ${score(j.score_avg)}${failedSlides ? ` · ${failedSlides} failing` : ""}`
        : "None yet",
    },
    { label: "Redos", value: String(j.redos) },
    { label: "Cost", value: formatMicros(j.cost_micros, currency) },
    {
      label: "Tokens",
      value: `${formatTokens(j.tokens_in)} in · ${formatTokens(j.tokens_out)} out`,
    },
    {
      label: "Delivery",
      value: j.delivery ? (
        <span data-slot="job-delivery">
          {[
            j.delivery.files !== null ? `${j.delivery.files} files` : null,
            j.delivery.thread_id ? `to ${j.delivery.thread_id}` : null,
            `${j.delivery.post_ids.length} post${j.delivery.post_ids.length === 1 ? "" : "s"}`,
          ]
            .filter(Boolean)
            .join(" · ")}
          <span className="text-muted-foreground"> · {formatDateTime(j.delivery.at)}</span>
        </span>
      ) : (
        "Not delivered yet"
      ),
    },
    { label: "Started", value: formatDateTime(j.started_at) },
    {
      label: j.ended_at ? "Ended" : "Running for",
      value: j.ended_at
        ? `${formatDateTime(j.ended_at)} (${formatElapsed(j.started_at, j.ended_at)})`
        : formatElapsed(j.started_at),
    },
  ];
  return (
    <section
      className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4"
      data-slot="job-card"
      data-job={j.job_id}
      data-status={j.status}
      aria-label={`Job ${j.kind ?? j.job_id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold capitalize">{j.kind ?? "Job"}</h3>
        <Badge variant={tone}>{label}</Badge>
        {j.stage ? (
          <Badge variant="outline" data-slot="job-stage">
            Now: {j.stage}
          </Badge>
        ) : null}
        <code className="ml-auto font-mono text-2xs text-muted-foreground">{j.job_id}</code>
      </div>
      <Facts items={facts} />
      {j.stages.length ? (
        <Table data-slot="job-stages">
          <TableCaption>Stages of the job, in order.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Stage</TableHead>
              <TableHead scope="col">Try</TableHead>
              <TableHead scope="col">Started</TableHead>
              <TableHead scope="col">Took</TableHead>
              <TableHead scope="col">Result</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {j.stages.map((s) => {
              const over =
                s.budget_s !== null &&
                Date.parse(s.ended_at ?? new Date().toISOString()) - Date.parse(s.started_at) >
                  s.budget_s * 1000;
              return (
                <TableRow key={`${s.stage}:${s.attempt}`} data-stage={s.stage}>
                  <TableHead scope="row" className="font-medium">
                    {s.stage}
                  </TableHead>
                  <TableCell className="tabular-nums">{s.attempt}</TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {formatDateTime(s.started_at)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {formatElapsed(s.started_at, s.ended_at)}
                    {over ? <span className="text-warning-text"> · over its budget</span> : null}
                  </TableCell>
                  <TableCell>
                    {s.ok === null ? (
                      <Badge variant="info">Running</Badge>
                    ) : s.ok ? (
                      <Badge variant="success">OK</Badge>
                    ) : (
                      <Badge variant="danger">Failed</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}
      {j.slides.length ? (
        <Table data-slot="job-slides">
          <TableCaption>Slide scores from the critic.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Slide</TableHead>
              <TableHead scope="col">Score</TableHead>
              <TableHead scope="col">Critic</TableHead>
              <TableHead scope="col">Result</TableHead>
              <TableHead scope="col">Redos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {j.slides.map((s) => (
              <TableRow key={s.slide} data-slide={s.slide}>
                <TableHead scope="row" className="font-medium tabular-nums">
                  {s.slide}
                </TableHead>
                <TableCell className="tabular-nums">{score(s.score)}</TableCell>
                <TableCell className="text-muted-foreground">{s.critic ?? "—"}</TableCell>
                <TableCell>
                  {s.pass === null ? (
                    "—"
                  ) : s.pass ? (
                    <Badge variant="success">Pass</Badge>
                  ) : (
                    <Badge variant="danger">Fail</Badge>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">{s.redos}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </section>
  );
}

/** The run's job cards (nothing when the host ran no detached job). */
export function RunJobs({ jobs, currency }: { jobs: MonitorJob[]; currency: string | undefined }) {
  if (!jobs.length) return null;
  return (
    <Section
      id="run-jobs"
      title={jobs.length === 1 ? "Job" : "Jobs"}
      description="Detached work the host ran for this run, from its own reports."
    >
      <div className="flex flex-col gap-3">
        {jobs.map((j) => (
          <JobCard key={j.job_id} job={j} currency={currency} />
        ))}
      </div>
    </Section>
  );
}

// Stop on host -----------------------------------------------------------------------------------

const STOP_STATE: Record<
  StopRequest["state"],
  [string, "warning" | "info" | "success" | "danger" | "neutral"]
> = {
  pending: ["Waiting for the bridge", "warning"],
  picked: ["Picked up", "info"],
  acked: ["Stopped", "success"],
  failed: ["Could not stop", "danger"],
  expired: ["Expired", "neutral"],
};

/**
 * "Stop on host": asks the DE's bridge to stop the run where it runs (`runs.requestStop`). The
 * bridge polls every 20 s; the request expires unpicked after 5 minutes. It does not end the
 * run's record: Stop run does that.
 */
export function StopOnHostButton({
  api,
  company,
  runId,
  live,
}: {
  api: Api;
  company: CompanyAccess;
  runId: string;
  /** A request the bridge has not answered yet. */
  live: StopRequest | undefined;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const stop = useMutation({
    mutationFn: (why: string) =>
      api.client.runs.requestStop({
        params: { co: company.id, id: runId },
        body: why.trim() ? { reason: why.trim() } : {},
      }),
    onSettled: () => invalidateMonitoring(qc, company.id),
  });
  const waiting = stop.isPending || Boolean(live);
  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={waiting}
        data-slot="stop-on-host"
      >
        <CircleStopIcon aria-hidden="true" />
        {stop.isPending ? "Asking…" : live ? "Stop asked" : "Stop on host"}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop this run on its host?</AlertDialogTitle>
            <AlertDialogDescription>
              The DE's bridge picks the request up within about 20 seconds and stops the run where
              it runs. If no bridge picks it up in 5 minutes, it expires. The run's record here
              stays open: Stop run ends it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Field>
            <FieldLabel htmlFor={reasonId}>Reason (optional)</FieldLabel>
            <Textarea
              id={reasonId}
              value={reason}
              maxLength={500}
              rows={2}
              onChange={(e) => setReason(e.target.value)}
            />
            <FieldDescription>Kept with the request in the audit log.</FieldDescription>
          </Field>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep running</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                stop.mutate(reason);
                setOpen(false);
                setReason("");
              }}
            >
              Stop on host
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** The run's stop requests, newest first: where each stands, who asked and when. */
export function StopRequests({ requests }: { requests: StopRequest[] }) {
  const { me } = useSession();
  if (!requests.length) return null;
  const mine = new Set(me.memberships.map((m) => m.membership_id));
  return (
    <Section id="run-stops" title="Stop on host" description="Stops asked of the DE's bridge.">
      <ul className="flex flex-col gap-2" data-slot="stop-requests">
        {requests.map((s) => {
          const [label, tone] = STOP_STATE[s.state];
          const who =
            s.source === "guard"
              ? "A guard rule"
              : s.requested_by_membership_id && mine.has(s.requested_by_membership_id)
                ? "You"
                : "A person";
          return (
            <li
              key={s.id}
              className="flex flex-col gap-1 rounded-lg border border-border bg-card px-4 py-3 text-sm"
              data-stop={s.id}
              data-state={s.state}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={tone}>{label}</Badge>
                <span>
                  {who} asked {formatDateTime(s.requested_at)}
                </span>
                {s.reason ? <span className="text-muted-foreground">· {s.reason}</span> : null}
              </div>
              <p className="text-xs text-muted-foreground tabular-nums">
                {s.state === "pending"
                  ? `The bridge has not picked it up yet. Expires ${formatDateTime(s.expires_at)}.`
                  : s.state === "expired"
                    ? `No bridge picked it up by ${formatDateTime(s.expires_at)}.`
                    : [
                        s.picked_at ? `Picked up ${formatDateTime(s.picked_at)}` : null,
                        s.acked_at ? `answered ${formatDateTime(s.acked_at)}` : null,
                        s.ack?.method ? `by ${s.ack.method}` : null,
                        s.ack?.latency_ms != null ? `in ${seconds(s.ack.latency_ms)}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                {s.ack?.message ? ` · ${s.ack.message}` : ""}
              </p>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

// Alerts -----------------------------------------------------------------------------------------

export const ALERT_KIND_LABELS: Record<MonitorAlert["kind"], string> = {
  stuck_run: "Stuck run",
  post_storm: "Post storm",
  job_stalled: "Job stalled",
  guard_tripped: "Guard tripped",
  bridge_silent: "Bridge silent",
  skill_leak_suspected: "Possible skill leak",
  extraction_probe: "Extraction probe",
  repeated_failures: "Stopped after repeated failures",
  // lane B (REQUEST-RECOVERY)
  recovery: "Request waits on a person",
  // TASK-BUDGETS
  task_budget: "Stopped at a task limit",
};

export const ALERT_STATE_LABELS: Record<MonitorAlert["state"], string> = {
  open: "Open",
  acked: "Acked",
  resolved: "Resolved",
};

/** The run's alerts that are not resolved, as one notice on top of the page. */
export function RunAlerts({ alerts }: { alerts: MonitorAlert[] }) {
  const active = alerts.filter((a) => a.state !== "resolved");
  if (!active.length) return null;
  const critical = active.some((a) => a.severity === "critical");
  return (
    <Notice
      tone={critical ? "danger" : "warning"}
      className="mb-6"
      data-slot="run-alerts"
      title={active.length === 1 ? "1 open alert" : `${active.length} open alerts`}
    >
      <ul className="flex flex-col gap-1">
        {active.map((a) => (
          <li key={a.id} data-alert={a.id} data-kind={a.kind}>
            <span className="font-medium">{ALERT_KIND_LABELS[a.kind]}:</span> {a.summary}
            <span className="text-muted-foreground">
              {" "}
              · {a.state === "acked" ? "acked" : "open"} since {formatDateTime(a.opened_at)}
            </span>
          </li>
        ))}
      </ul>
    </Notice>
  );
}
